import React from "react";
import { modelsDelete } from "../../../../api/api";
import { useExternalModels, useModels } from "../../../../state/api/ai/useModels";
import { useModelPrices } from "../../../../state/api/ai/useModelPrices";
import { Doc, EffortLabelMap, Model, ModelPrice } from "../../../../types";
import DeleteButton from "../../buttons/DeleteButton";
import PageLoader from "../../loader/PageLoader";
import Pill from "../../pill/Pill";
import EditModelButton from "./EditModelButton";

export function formatPrice(p: ModelPrice) {
  return `$${p.ppmInputTokens} / $${p.ppmInputTokensCacheWrite5m} / $${p.ppmInputTokensCacheWrite1h} / $${p.ppmInputTokensCacheRead} / $${p.ppmOutputTokens}`;
}

interface Props {
  model: Doc<Model>;
}

function ModelCard(props: Props) {

  const {
    model
  } = props;

  const { reloadModels } = useModels();
  const { modelPricesById } = useModelPrices();
  const { externalModels } = useExternalModels();

  const external = externalModels.find(x => x.id === model.externalModelId);
  const modelInfo = external?.display_name ?? model.externalModelId;

  const price = modelPricesById[model.price];

  return (
    <div className="border rounded p-3 d-flex flex-column gap-2">
      <div className="d-flex flex-row gap-4 align-items-center justify-content-between">
        <div className="d-flex flex-column">
          <h6>{model.label || model.externalModelId}</h6>
          <div className="d-flex flex-row gap-1">
            {
              !!model.effort && <Pill>{EffortLabelMap[model.effort]}</Pill>
            }
            {
              model.thinking === true && <Pill>Thinking</Pill>
            }
            {
              model.thinking === false && <Pill>No thinking</Pill>
            }
            {
              !!model.directToolUseOnly && <Pill>Direct Tool Use</Pill>
            }
          </div>
        </div>
        <div className="d-flex flex-row gap-1">
          <EditModelButton model={model} duplicate />
          <EditModelButton model={model} />
          <DeleteButton
            apiCall={() => modelsDelete(model._id)}
            afterDelete={reloadModels}
          />
        </div>
      </div>
      <div style={{ height: "1px", width: "100%", backgroundColor: "var(--gray-100)" }} />
      <div className="d-flex flex-column gap-1">
        <span className="font-code font-s">{modelInfo}</span>
        <span className="font-code font-s">{model.maxOutputTokens.toLocaleString()} max tokens</span>
        <span className="font-code font-s">
          {
            price
              ? (price.label ? `${price.label} — ${formatPrice(price)}` : formatPrice(price))
              : "Price missing"
          }
        </span>
        {
          !!model.maxThinkingBudget && (
            <span className="font-code font-s">Thinking budget: {model.maxThinkingBudget.toLocaleString()} tokens</span>
          )
        }
      </div>
    </div>
  )
}

export default function ModelManagement() {

  const { models, loadingModels } = useModels();
  const { loadingModelPrices } = useModelPrices();
  const { loadingExternalModels } = useExternalModels();

  if (loadingModels || loadingModelPrices || loadingExternalModels) return <PageLoader />;

  return (
    <div className="d-flex flex-column gap-3 ps-4 pe-4 ps-sm-3 pe-sm-3 pt-4" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
      {
        models?.length
          ? (
            models.map(x => (
              <ModelCard
                key={x._id}
                model={x}
              />
            ))
          )
          : <span>No models</span>
      }
    </div>
  )
}
